/**
 * DERIVED HOOKUPS (2026-10-04, Matt: "For any wall-hung TVs, I want there to
 * automatically be power and data outlets in the wall behind. For any nightstands next
 * to beds … outlets for table lamps behind them. For a range/oven … power and gas …
 * refrigerators … power and dedicated water line. At washing machines … water and
 * sewer connections as well as power and for dryers … gas and dedicated power …"
 * then "Please build them all" — every hookup the library's equipment implies).
 *
 * The ONE rule set: an object's kind (+ its fuel, + its neighbours for the
 * bed/nightstand/sofa cases) → the services it needs, each placed in the object's own
 * LOCAL frame (objectKinds.ts: x across the width centred on 0, y from the back face
 * toward the front, z above the object's level floor). Nothing is stored — plan
 * glyphs, 3D stub-outs, the Properties list and the Hookup Schedule all read this.
 * The one stored thing is a per-object OFF list (`hookupsOff`, instance data) so a
 * fridge with no water nearby can drop its line.
 */
import type { AssemblyRecord, ParamValue, Path2D, Pt2 } from './types'
import { WALL_MOUNT_DEFAULT_FT, isGlassCooktop } from './categories/object/objectKinds'
import { WALL_HUNG, wallHungRimIn, wallHungRoughIn } from './categories/object/wallHungToilet'
import { centerlineOffsetFt } from './locationLine'
import { flattenPathPts } from './arc'
import { polylinePointAt } from './pathOps'
import {
  isGarageOpenerOp, garageOpenerLayout, GARAGE_BUTTON_Z_FT, GARAGE_KEYPAD_Z_FT, GARAGE_KEYPAD_PAST_FT,
  GARAGE_PANEL_LAP_FT,
} from './garageOpener'

export type HookupKind =
  | 'power' | 'powerDedicated' | 'power240' | 'data' | 'gas'
  | 'cold' | 'hot' | 'drain' | 'floorDrain' | 'vent' | 'flue' | 'condensate'
  | 'lineSet' | 'disconnect' | 'floorBox' | 'poolPlumbing'
  // RESIDENTIAL ELEVATOR WIRING (2026-10-06): the pit-light switch and the hall call station
  | 'switch' | 'callStation'
  // GARAGE DOOR OPENER (2026-10-09): the wall control inside, the keypad outside
  | 'garageButton' | 'garageKeypad'

export interface Hookup {
  /** stable within the object — the key `hookupsOff` stores */
  key: string
  kind: HookupKind
  /** local frame: across the width (x), off the back face (y, 0 = the wall behind), height above the level */
  x: number
  y: number
  z: number
  /** what it is, for the Properties list and the schedule */
  label: string
  /** the specification line ("240V / 30A dedicated", "1/2\" gas valve") */
  spec: string
  /** true when the user turned it off on this object */
  off?: boolean
  /** RESIDENTIAL ELEVATOR WIRING (2026-10-06): the level this service belongs to when it
   *  is not the equipment's own (an elevator's top-stop disconnect, its upper hall call
   *  stations) — plans print it on that level only, the schedule files it there */
  levelId?: string
  /** the direction to look for its wall, in the object's local frame (default: back),
   *  from the local point (0, y) */
  seek?: 'back' | 'left' | 'right'
  /** take the FAR face of the first wall hit (the room beyond it — a machine room
   *  beside a hoistway), not the face looking back at the equipment */
  outside?: boolean
  /** FREESTANDING TUB (2026-10-06): comes up through the FLOOR at its own (x, y) — a
   *  floor-mounted filler's supplies — never looks for a wall */
  floor?: boolean
  /** already placed by its rule (a hall call station beside a landing door). GARAGE DOOR
   *  OPENER: `host` / `zAbs` place a ceiling service the rule found itself (the opener's
   *  receptacle over its motor) */
  fixed?: { at: Pt2; n: Pt2; hostId: string | null; extraIds?: string[]; host?: HookupHost; zAbs?: number }
}

/** per kind: the drawing label, the 3D stub colour key and a default spec */
export const HOOKUP_KINDS: Record<HookupKind, { label: string; tag: string; spec: string }> = {
  power: { label: 'Power', tag: '', spec: '120V duplex receptacle' },
  powerDedicated: { label: 'Dedicated power', tag: 'D', spec: '120V / 20A dedicated receptacle' },
  power240: { label: '240V power', tag: '', spec: '240V receptacle, dedicated circuit' },
  data: { label: 'Data', tag: '', spec: 'Data / low-voltage outlet' },
  gas: { label: 'Gas', tag: 'G', spec: 'Gas stub with shut-off valve' },
  cold: { label: 'Cold water', tag: 'CW', spec: 'Cold water supply with stop' },
  hot: { label: 'Hot water', tag: 'HW', spec: 'Hot water supply with stop' },
  drain: { label: 'Drain', tag: 'W', spec: 'Waste / drain connection' },
  floorDrain: { label: 'Floor drain', tag: '', spec: 'Drain at the floor' },
  vent: { label: 'Vent / duct', tag: 'V', spec: 'Exhaust duct to the exterior' },
  flue: { label: 'Flue', tag: 'F', spec: 'Flue / combustion vent' },
  condensate: { label: 'Condensate', tag: 'CD', spec: 'Condensate drain' },
  lineSet: { label: 'Refrigerant line set', tag: 'RL', spec: 'Refrigerant line set to the paired unit' },
  disconnect: { label: 'Disconnect', tag: 'DS', spec: 'Fused disconnect within sight' },
  floorBox: { label: 'Floor outlet', tag: '', spec: 'Floor box: 120V duplex' },
  poolPlumbing: { label: 'Pool plumbing', tag: 'P', spec: 'Pool supply / return lines' },
  switch: { label: 'Switch', tag: 'S', spec: 'Light switch' },
  callStation: { label: 'Hall call station', tag: 'HC', spec: 'Elevator hall call station' },
  garageButton: { label: 'Garage door control', tag: 'GD', spec: 'Lighted wall control for the garage door opener, 5\'-0" to centre (UL 325)' },
  garageKeypad: { label: 'Garage door keypad', tag: 'KP', spec: 'Wireless entry keypad for the garage door opener, on the exterior jamb' },
}

/** the kinds that run on a fuel choice, and their default */
export const FUEL_KINDS: Record<string, 'gas' | 'electric'> = {
  range: 'gas', dryer: 'gas', waterHeater: 'gas', tanklessWaterHeater: 'gas', furnace: 'gas',
  washerDryerCombo: 'electric',
  // COOKTOPS: blank Fuel follows the cooktop's style (gas / rangetop vs induction / radiant)
  cooktop: 'gas',
}


/** the minimal element view this module reads (a ResolvedElement satisfies it) */
export interface HookupElementLike {
  id: string
  category: string
  levelId: string
  params: Record<string, unknown>
  typeParams: Record<string, unknown>
  assemblyThicknessFt?: number
  assembly?: AssemblyRecord | null
  level?: { elevationFt: number } | null
  paramLevels?: Record<string, { elevationFt: number }>
  /** a hosted element's host (a door's wall) */
  hostId?: string | null
}

const num = (v: unknown, d: number) => (typeof v === 'number' && Number.isFinite(v) ? v : d)
/** inches as a drafting fraction to the 1/8" ("4-1/2\"") */
const fmtIn = (inch: number): string => {
  const e = Math.round(inch * 8), whole = Math.floor(e / 8), f = e % 8
  if (!f) return `${whole}"`
  const g = f % 4 === 0 ? 4 : f % 2 === 0 ? 2 : 1
  return `${whole ? whole + '-' : ''}${f / g}/${8 / g}"`
}

/** an object's anchor, facing and size — its local frame in the world */
export function hookupFrame(el: HookupElementLike): { o: Pt2; u: Pt2; v: Pt2; w: number; d: number } | null {
  const path = el.params.path as { nodes?: { pt: Pt2 }[] } | null
  const o = path?.nodes?.[0]?.pt
  if (!o) return null
  const r = (num(el.params.rotationDeg, 0) * Math.PI) / 180
  const u: Pt2 = [Math.cos(r), Math.sin(r)]
  const v: Pt2 = [-Math.sin(r), Math.cos(r)]
  return { o: [o[0], o[1]], u, v, w: num(el.typeParams.widthFt, 2), d: num(el.typeParams.depthFt, 2) }
}

/** a local (x, y) point → world */
export function hookupWorld(f: { o: Pt2; u: Pt2; v: Pt2 }, x: number, y: number): Pt2 {
  return [f.o[0] + f.u[0] * x + f.v[0] * y, f.o[1] + f.u[1] * x + f.v[1] * y]
}

/** a world point → local (x, y) */
function toLocal(f: { o: Pt2; u: Pt2; v: Pt2 }, p: Pt2): Pt2 {
  const dx = p[0] - f.o[0], dy = p[1] - f.o[1]
  return [dx * f.u[0] + dy * f.u[1], dx * f.v[0] + dy * f.v[1]]
}

const kindOf = (el: HookupElementLike) => String(el.typeParams.objectKind || '')

/** a nightstand standing beside a bed on its own level: which side of the bed */
function nightstandSide(bed: HookupElementLike, ns: HookupElementLike): -1 | 1 | 0 {
  if (bed.levelId !== ns.levelId) return 0
  const fb = hookupFrame(bed), fn = hookupFrame(ns)
  if (!fb || !fn) return 0
  // the nightstand's middle in the bed's frame: beside it (past either side, within
  // 2'), toward its head (the first 3' off the headboard wall)
  const c = toLocal(fb, hookupWorld(fn, 0, fn.d / 2))
  const beyond = Math.abs(c[0]) - fb.w / 2
  if (beyond < -0.25 || beyond > 2 + fn.w / 2) return 0
  if (c[1] < -1 || c[1] > 3.5) return 0
  return c[0] < 0 ? -1 : 1
}

/** distance from p to a polyline */
function distToPolyline(p: Pt2, pts: Pt2[]): number {
  let best = Infinity
  for (let i = 0; i + 1 < pts.length; i++) {
    const a = pts[i], b = pts[i + 1], dx = b[0] - a[0], dy = b[1] - a[1], L2 = dx * dx + dy * dy
    const t = L2 > 0 ? Math.max(0, Math.min(1, ((p[0] - a[0]) * dx + (p[1] - a[1]) * dy) / L2)) : 0
    best = Math.min(best, Math.hypot(p[0] - (a[0] + dx * t), p[1] - (a[1] + dy * t)))
  }
  return best
}

/** a sofa with no wall behind it (floating in the room) */
function floating(el: HookupElementLike, others: Iterable<HookupElementLike>): boolean {
  const f = hookupFrame(el)
  if (!f) return false
  const back = hookupWorld(f, 0, -0.05)
  for (const w of others) {
    if (w.category !== 'wall' || w.levelId !== el.levelId) continue
    const nodes = (w.params.path as { nodes?: { pt: Pt2 }[] } | null)?.nodes
    if (!nodes || nodes.length < 2) continue
    if (distToPolyline(back, nodes.map((n) => n.pt)) <= num(w.assemblyThicknessFt, 0.5) / 2 + 1) return false
  }
  return true
}

/**
 * RESIDENTIAL ELEVATOR WIRING (2026-10-06, Matt: "the elevator family to show the required
 * electrical connection on the plans on the wall it would go onto. Typical for a
 * residential elevator. If it should have options based on how they typically work,
 * provide them. … the controls to go on the wall next to the strike side of the door that
 * leads into the shaft at the proper height for all levels it serves").
 *
 * The DRIVE decides where the controller lives and so where the power lands — the typical
 * layouts in the residential manufacturers' planning guides and NEC Article 620:
 *  - hydraulic: pump + controller in a MACHINE ROOM beside the hoistway at the bottom
 *    landing — a 208/230V single-phase main (typ. 40A) through a lockable fused
 *    disconnect in that room, a separate 120V/15A cab-light circuit with its own
 *    disconnect, and a phone line for the cab's emergency phone (ASME A17.1 §5.3);
 *  - machineRoomLess (cable / chain / gear drive): the controller in a cabinet at the TOP
 *    landing beside the hoistway — the same three services there, main typ. 30A;
 *  - both hoistway drives also get the PIT: a 120V GFCI receptacle (NEC 620.85) and the
 *    pit light, switched from the pit access at the bottom landing (NEC 620.24);
 *  - pneumatic (vacuum tube, no pit, no hoistway): one 208/230V dedicated circuit with a
 *    disconnect at the top unit, on the wall the tube stands against;
 *  - throughFloor (shaftless platform lift): a 120V dedicated receptacle it plugs into.
 * The controller-side services land on the hoistway's CONTROLLER-SIDE wall — its far face
 * (the machine room / controller closet beyond it) for the disconnects and phone, its
 * shaft face for the pit items. Amperages are the typical figures; the spec lines say
 * to confirm with the manufacturer.
 *
 * The CONTROLS: a hall call station at every landing door into the shaft, on the landing
 * face of that door's wall, on its STRIKE side (away from the hinges), centred 9" past
 * the clear opening, 42" to centre (the ADA reach height residential manufacturers
 * mount to) — found from the real doors, so moving or re-handing a door moves its call
 * station, and a level with no door into the shaft gets none.
 */
export const ELEVATOR_DRIVES = [
  { value: 'hydraulic', label: 'Hydraulic — machine room' },
  { value: 'machineRoomLess', label: 'Cable / chain drive — no machine room' },
  { value: 'pneumatic', label: 'Pneumatic vacuum (no pit)' },
  { value: 'throughFloor', label: 'Through-floor lift (no shaft)' },
] as const
/** hall call station centreline above each landing floor */
export const ELEVATOR_CALL_HEIGHT_FT = 3.5
/** the call station's centre past the clear opening edge, along the wall */
const CALL_PAST_OPENING_FT = 0.75

/** an elevator stop, as doc.ts resolves it onto the instance (`elevatorStops`) */
interface ElevatorStop { id: string; name: string; elevationFt: number }

function elevatorStops(el: HookupElementLike): ElevatorStop[] {
  const raw = el.params.elevatorStops
  if (!Array.isArray(raw)) return []
  return (raw as unknown[]).filter((x): x is ElevatorStop =>
    !!x && typeof (x as ElevatorStop).id === 'string' && typeof (x as ElevatorStop).elevationFt === 'number')
}

function pathLength(pts: Pt2[]): number {
  let L = 0
  for (let i = 1; i < pts.length; i++) L += Math.hypot(pts[i][0] - pts[i - 1][0], pts[i][1] - pts[i - 1][1])
  return L
}

/** a hall call station beside every landing door into this elevator's shaft, per stop */
function elevatorCallStations(el: HookupElementLike, list: HookupElementLike[], stops: ElevatorStop[]): Hookup[] {
  const f = hookupFrame(el)
  if (!f) return []
  const base = el.level?.elevationFt ?? 0
  const byId = new Map(list.map((x) => [x.id, x]))
  const centre = hookupWorld(f, 0, f.d / 2)
  const callZ = num(el.params.elevatorCallHeightFt, ELEVATOR_CALL_HEIGHT_FT)
  const out: Hookup[] = []
  for (const stop of stops) {
    for (const door of list) {
      if (door.category !== 'door' || door.levelId !== stop.id || !door.hostId) continue
      const wall = byId.get(door.hostId)
      const path = wall?.params.path as Path2D | null | undefined
      if (!wall || !path?.nodes || path.nodes.length < 2) continue
      const pts = flattenPathPts(path)
      const s = num(door.params.hostOffsetFt, 0)
      const { pt: P, dir } = polylinePointAt(pts, s)
      // a door INTO the shaft: on the hoistway's outline (within a wall's reach of an
      // edge), neither out in the room nor buried inside the shaft
      const c = toLocal(f, P)
      const outBy = Math.max(Math.abs(c[0]) - f.w / 2, -c[1], c[1] - f.d)
      if (outBy > 1.25 || outBy < -0.6) continue
      const nl: Pt2 = [-dir[1], dir[0]]
      // the landing side = the wall face looking away from the shaft
      const sgn = (P[0] - centre[0]) * nl[0] + (P[1] - centre[1]) * nl[1] >= 0 ? 1 : -1
      const delta = centerlineOffsetFt(wall.params.locationLine as ParamValue, wall.params.flipped as ParamValue, wall.assembly ?? null)
      const off = delta + sgn * num(wall.assemblyThicknessFt, 0.5) / 2
      // the strike side: a left-hand door hinges at the lower station (plan.ts's
      // doorPlanSymbol convention), so it latches toward the upper one
      const dw = num(door.params.widthFt, num(door.typeParams.widthFt, 3))
      const total = pathLength(pts)
      let side = door.params.hand === 'R' ? -1 : 1
      let sc = s + side * (dw / 2 + CALL_PAST_OPENING_FT)
      // no wall left on the strike side (the door hard against a corner): the hinge side
      if (sc < 0.2 || sc > total - 0.2) { side = -side; sc = s + side * (dw / 2 + CALL_PAST_OPENING_FT) }
      if (sc < 0.2 || sc > total - 0.2) continue
      const q = polylinePointAt(pts, sc).pt
      out.push({
        key: `call:${door.id}`, kind: 'callStation', x: 0, y: 0,
        z: stop.elevationFt - base + callZ,
        label: stops.length > 1 ? `Hall call station — ${stop.name}` : 'Hall call station',
        spec: 'Elevator hall call station, strike side of the landing door, 42" to centre',
        levelId: stop.id,
        fixed: { at: [q[0] + nl[0] * off, q[1] + nl[1] * off], n: [nl[0] * sgn, nl[1] * sgn], hostId: wall.id, extraIds: [door.id] },
      })
    }
  }
  return out
}

/**
 * GARAGE DOOR OPENER (2026-10-09, Matt): a sectional garage door's opener services — the
 * same derived, hosted, never-stored hookups an appliance gets, placed by the door's own
 * rule (every one `fixed`):
 *  - the opener's 120V receptacle on the ceiling over its motor head (garageOpener.ts's
 *    layout, measured off the inside face of the door's wall); no ceiling there, it hangs
 *    just over the motor;
 *  - the lighted wall control on the garage side of the main door into the house, on its
 *    latch side, 5'-0" up (UL 325's minimum) — the house door is the nearest ordinary
 *    door on the garage door's level standing on its inside; with none, beside the garage
 *    door itself, inside;
 *  - the keypad on the exterior face of the wall, beside the jamb on the LEFT as you face
 *    the door from outside, 5'-0" up.
 * Moving the garage door, re-handing the house door or moving either wall moves them.
 */
const GARAGE_FAMILY_OPS = new Set(['sectional', 'schweiss', 'carriage', 'gateSwing', 'gateSlide'])

function garageDoorHookups(el: HookupElementLike, list: HookupElementLike[]): Hookup[] {
  if (!isGarageOpenerOp(el.typeParams.operation) || !el.hostId) return []
  const byId = new Map(list.map((x) => [x.id, x]))
  const wall = byId.get(el.hostId)
  const path = wall?.params.path as Path2D | null | undefined
  if (!wall || !path?.nodes || path.nodes.length < 2) return []
  const pts = flattenPathPts(path)
  const total = pathLength(pts)
  const s = num(el.params.hostOffsetFt, 0)
  const { pt: P, dir } = polylinePointAt(pts, s)
  const nl: Pt2 = [-dir[1], dir[0]]
  const fs = el.params.facing === 'out' ? -1 : 1
  const delta = centerlineOffsetFt(wall.params.locationLine as ParamValue, wall.params.flipped as ParamValue, wall.assembly ?? null)
  const half = num(wall.assemblyThicknessFt, 0.5) / 2
  const inFace = delta + fs * half, outFace = delta - fs * half
  const inN: Pt2 = [nl[0] * fs, nl[1] * fs]
  const outN: Pt2 = [-inN[0], -inN[1]]
  const dw = num(el.params.widthFt, num(el.typeParams.widthFt, 16))
  const dh = num(el.params.heightFt, num(el.typeParams.heightFt, 7))
  const base = el.level?.elevationFt ?? 0
  const sill = num(el.params.sillFt, 0)
  const out: Hookup[] = []
  const at = (sAlong: number, off: number): Pt2 => {
    const q = polylinePointAt(pts, Math.max(0, Math.min(total, sAlong))).pt
    return [q[0] + nl[0] * off, q[1] + nl[1] * off]
  }

  // the opener's receptacle over the motor head
  const L = garageOpenerLayout(0, dh + GARAGE_PANEL_LAP_FT, 0.146)
  const facePt = at(s, inFace)
  const motor: Pt2 = [facePt[0] + inN[0] * L.motorD, facePt[1] + inN[1] * L.motorD]
  const ce = slabAt(list, 'ceiling', el.levelId, motor)
  const ceZ = ce ? (ce.level?.elevationFt ?? base) + num(ce.params.heightOffsetFt, 8) : null
  out.push({
    key: 'opener', kind: 'power', x: 0, y: 0, z: 0, label: 'Garage door opener',
    spec: '120V GFCI receptacle at the ceiling over the opener motor head',
    fixed: {
      at: motor, n: inN, hostId: ce ? ce.id : null, host: 'ceiling',
      zAbs: ceZ != null && ceZ > base + sill + L.motorZ1 ? ceZ : base + sill + L.motorZ1 + 1.5,
    },
  })

  // the keypad: outside, beside the jamb on the left of someone facing the door from outside
  // (facing into the garage, their left runs −fs along the wall direction)
  let kSide = -fs
  let kS = s + kSide * (dw / 2 + GARAGE_KEYPAD_PAST_FT)
  if (kS < 0.2 || kS > total - 0.2) { kSide = -kSide; kS = s + kSide * (dw / 2 + GARAGE_KEYPAD_PAST_FT) }
  if (kS >= 0.2 && kS <= total - 0.2) {
    out.push({
      key: 'keypad', kind: 'garageKeypad', x: 0, y: 0, z: GARAGE_KEYPAD_Z_FT,
      label: HOOKUP_KINDS.garageKeypad.label, spec: HOOKUP_KINDS.garageKeypad.spec,
      fixed: { at: at(kS, outFace), n: outN, hostId: wall.id },
    })
  }

  // the wall control at the house door: the nearest ordinary door on this level that stands
  // on the garage side of the garage door's wall
  const inside: Pt2 = [facePt[0] + inN[0] * 3, facePt[1] + inN[1] * 3]
  let best: { d: number; door: HookupElementLike; wall: HookupElementLike; pts: Pt2[]; s: number } | null = null
  for (const d of list) {
    if (d.id === el.id || d.category !== 'door' || d.levelId !== el.levelId || !d.hostId) continue
    if (GARAGE_FAMILY_OPS.has(String(d.typeParams.operation))) continue
    const w = byId.get(d.hostId)
    const wp = w?.params.path as Path2D | null | undefined
    if (!w || !wp?.nodes || wp.nodes.length < 2) continue
    const dpts = flattenPathPts(wp)
    const ds = num(d.params.hostOffsetFt, 0)
    const Q = polylinePointAt(dpts, ds).pt
    // on the garage side of the garage door's wall, and not in that same wall
    const into = (Q[0] - facePt[0]) * inN[0] + (Q[1] - facePt[1]) * inN[1]
    if (into < 1) continue
    const dist = Math.hypot(Q[0] - inside[0], Q[1] - inside[1])
    if (dist > 60) continue
    if (!best || dist < best.d) best = { d: dist, door: d, wall: w, pts: dpts, s: ds }
  }
  if (best) {
    const w = best.wall
    const { pt: Q, dir: qd } = polylinePointAt(best.pts, best.s)
    const qn: Pt2 = [-qd[1], qd[0]]
    // the garage side of the house door's wall: the face looking toward the garage door
    const sgn = (inside[0] - Q[0]) * qn[0] + (inside[1] - Q[1]) * qn[1] >= 0 ? 1 : -1
    const wDelta = centerlineOffsetFt(w.params.locationLine as ParamValue, w.params.flipped as ParamValue, w.assembly ?? null)
    const off = wDelta + sgn * num(w.assemblyThicknessFt, 0.5) / 2
    const ddw = num(best.door.params.widthFt, num(best.door.typeParams.widthFt, 3))
    const wTotal = pathLength(best.pts)
    // its latch side (a left-hand door hinges at the lower station — elevatorCallStations)
    let side = best.door.params.hand === 'R' ? -1 : 1
    let sc = best.s + side * (ddw / 2 + 0.6)
    if (sc < 0.2 || sc > wTotal - 0.2) { side = -side; sc = best.s + side * (ddw / 2 + 0.6) }
    if (sc >= 0.2 && sc <= wTotal - 0.2) {
      const q = polylinePointAt(best.pts, sc).pt
      out.push({
        key: 'button', kind: 'garageButton', x: 0, y: 0, z: GARAGE_BUTTON_Z_FT,
        label: HOOKUP_KINDS.garageButton.label, spec: HOOKUP_KINDS.garageButton.spec,
        fixed: { at: [q[0] + qn[0] * off, q[1] + qn[1] * off], n: [qn[0] * sgn, qn[1] * sgn], hostId: w.id, extraIds: [best.door.id] },
      })
      return out
    }
  }
  // no house door found: beside the garage door, inside, opposite the keypad
  const bS = s - kSide * (dw / 2 + GARAGE_KEYPAD_PAST_FT)
  if (bS >= 0.2 && bS <= total - 0.2) {
    out.push({
      key: 'button', kind: 'garageButton', x: 0, y: 0, z: GARAGE_BUTTON_Z_FT,
      label: HOOKUP_KINDS.garageButton.label, spec: HOOKUP_KINDS.garageButton.spec,
      fixed: { at: at(bS, inFace), n: inN, hostId: wall.id },
    })
  }
  return out
}

/**
 * Every hookup this object implies, the user's OFF ones flagged (`off: true`).
 * `others` = the model's other elements (beds/nightstands/walls are read for the
 * adjacency rules; pass the resolved list once and reuse it).
 */
export function objectHookups(el: HookupElementLike, others: Iterable<HookupElementLike> = []): Hookup[] {
  if (el.category === 'door') {
    const hs = garageDoorHookups(el, [...others])
    const off = new Set(Array.isArray(el.params.hookupsOff) ? (el.params.hookupsOff as string[]) : [])
    for (const hk of hs) if (off.has(hk.key)) hk.off = true
    return hs
  }
  if (el.category !== 'object') return []
  const t = el.typeParams, p = el.params
  const kind = kindOf(el)
  const w = num(t.widthFt, 2), h = num(t.heightFt, 2.5)
  const fuel = (t.fuel === 'electric' || t.fuel === 'gas') ? t.fuel : (FUEL_KINDS[kind] ?? 'gas')
  const mount = num(t.mountHeightFt, 0) > 0 ? num(t.mountHeightFt, 0) : (WALL_MOUNT_DEFAULT_FT[kind] ?? null)
  const out: Hookup[] = []
  const add = (key: string, k: HookupKind, x: number, z: number, spec?: string, y = 0) =>
    out.push({ key, kind: k, x, y, z, label: HOOKUP_KINDS[k].label, spec: spec ?? HOOKUP_KINDS[k].spec })
  const side = (f: number) => Math.max(-w / 2 + 0.2, Math.min(w / 2 - 0.2, f))
  // the neighbour list is read only by the bed / nightstand / sofa rules — copied lazily
  let listCache: HookupElementLike[] | null = null
  const list = () => (listCache ??= [...others])
  switch (kind) {
    case 'wallTV': {
      const zc = (mount ?? 4)
      add('power', 'power', side(-0.3), zc, 'Recessed TV box: 120V duplex')
      add('data', 'data', side(0.3), zc, 'Recessed TV box: HDMI / data')
      break
    }
    case 'nightstand': {
      const bed = list().find((b) => b.category === 'object' && kindOf(b) === 'bed' && nightstandSide(b, el) !== 0)
      if (bed) add('power', 'power', 0, 1.5, '120V duplex for a table lamp')
      break
    }
    case 'bed': {
      const sides = new Set(list().filter((n) => n.category === 'object' && kindOf(n) === 'nightstand').map((n) => nightstandSide(el, n)))
      if (!sides.has(-1)) add('powerL', 'power', -w / 2 - 0.6, 1.5, '120V duplex beside the headboard')
      if (!sides.has(1)) add('powerR', 'power', w / 2 + 0.6, 1.5, '120V duplex beside the headboard')
      break
    }
    case 'desk':
      add('power', 'power', side(-0.4), 1.5, '120V duplex above the desk')
      add('data', 'data', side(0.4), 1.5)
      break
    case 'sofa': case 'sectional':
      if (floating(el, list())) add('floor', 'floorBox', 0, 0, undefined, -0.5)
      break
    case 'cooktop': {
      // COOKTOPS: inside the base cabinet under it — a gas stub + 120V for the igniters, or
      // a 240V circuit for an induction / radiant top (its style decides, unless Fuel is set)
      const gas = (t.fuel === 'electric' || t.fuel === 'gas') ? t.fuel === 'gas' : !isGlassCooktop(t)
      if (gas) {
        add('gas', 'gas', side(-0.5), 1.5, '1/2" gas stub with shut-off in the cabinet below the cooktop')
        add('power', 'power', side(0.5), 1.5, '120V receptacle in the cabinet below (igniters)')
      } else add('power', 'power240', 0, 1.5, '240V / 40A circuit, cooktop junction box in the cabinet below')
      break
    }
    case 'range':
      if (fuel === 'gas') {

        add('gas', 'gas', side(-0.6), 0.5, '1/2" gas stub with shut-off behind the range')
        add('power', 'power', side(0.6), 0.5, '120V receptacle (igniters, oven)')
      } else add('power', 'power240', 0, 0.5, '240V / 50A range receptacle')
      break
    case 'hoodInsert': {
      const zb = num(p.hoodBottomFt, 5.5)
      add('power', 'powerDedicated', side(0.4), zb + h + 0.5, '120V dedicated, hood blower')
      add('vent', 'vent', 0, zb + h + 1, 'Hood duct to the exterior (size per manufacturer)')
      break
    }
    case 'fridge': case 'columnFridge':
      add('power', 'powerDedicated', side(0.5), 3.5, '120V / 20A dedicated receptacle')
      add('water', 'cold', side(-0.5), 0.5, 'Recessed ice-maker box: 1/4" cold water')
      break
    case 'beverageCenter':
      add('power', 'power', 0, 0.75, '120V receptacle behind the unit')
      break
    case 'iceMaker':
      add('power', 'powerDedicated', side(0.5), 0.75)
      add('water', 'cold', side(-0.4), 0.75, '1/4" cold water with stop')
      add('drain', 'drain', 0, 0.5, 'Drain (gravity or pump, per manufacturer)')
      break
    case 'microwaveDrawer':
      add('power', 'powerDedicated', 0, 0.75, '120V / 20A dedicated receptacle')
      break
    case 'builtInAppliance': {
      // CASEWORK STUDIO stage B: by the unit in the opening (caseworkAppliances.ts)
      const u = String(t.applianceUnit || ''), ak = String(t.applianceKind || '')
      const panelFridge = /fridge|freezer|ucFridge/i.test(ak)
      if (u === 'oven' || u === 'ovenDouble' || u === 'speedOven' || u === 'steamOven') {
        add('power', 'power240', 0, h + 0.5, '240V / 30A dedicated, oven junction box')
        if (u === 'steamOven') add('water', 'cold', side(-0.4), h / 2, '1/4" cold water with stop (or a reservoir model)')
      } else if (u === 'microwave' || u === 'microwaveDrawer') {
        add('power', 'powerDedicated', 0, Math.min(h, 0.75), '120V / 20A dedicated receptacle')
      } else if (u === 'coffee') {
        add('power', 'powerDedicated', side(0.4), h / 2, '120V / 20A dedicated receptacle')
        add('water', 'cold', side(-0.4), h / 2, '1/4" cold water with stop (plumbed coffee system)')
      } else if (u === 'warmingDrawer' || u === 'cupWarmer' || u === 'vacuumDrawer' || u === 'wine') {
        add('power', 'power', 0, Math.min(h, 0.75), '120V receptacle behind the unit')
      } else if (u.startsWith('fridge') || (panelFridge && ak.startsWith('column'))) {
        add('power', 'powerDedicated', side(0.5), Math.min(h, 3.5), '120V / 20A dedicated receptacle')
        add('water', 'cold', side(-0.5), 0.5, 'Recessed ice-maker box: 1/4" cold water')
      } else if (ak === 'iceMaker') {
        add('power', 'powerDedicated', side(0.5), 0.75)
        add('water', 'cold', side(-0.4), 0.75, '1/4" cold water with stop')
        add('drain', 'drain', 0, 0.5, 'Drain (gravity or pump, per manufacturer)')
      } else if (ak === 'dishwasher' || ak === 'dishDrawers') {
        add('power', 'powerDedicated', side(0.5), 0.5, '120V / 20A dedicated (in the sink base)')
        add('hot', 'hot', side(-0.4), 0.5, 'Hot water with stop (in the sink base)')
        add('drain', 'drain', 0, 0.75, 'Drain to the sink tailpiece / air gap')
      } else if (panelFridge || ak === 'wineColumn') {
        add('power', 'powerDedicated', side(0.5), Math.min(h, 0.75), '120V / 20A dedicated receptacle')
      }
      break
    }
    case 'wallOven':
      add('power', 'power240', 0, (mount ?? 4) + h / 2 + 0.5, '240V / 30A dedicated, oven junction box')
      break
    case 'dishwasher':
      add('power', 'powerDedicated', side(0.5), 0.5, '120V / 20A dedicated (in the sink base)')
      add('hot', 'hot', side(-0.4), 0.5, 'Hot water with stop (in the sink base)')
      add('drain', 'drain', 0, 0.75, 'Drain to the sink tailpiece / air gap')
      break
    case 'kitchenSink':
      add('hot', 'hot', side(-0.4), 1.6)
      add('cold', 'cold', side(0.4), 1.6)
      add('drain', 'drain', 0, 1.35, '1-1/2" trap arm')
      add('disposal', 'power', side(0.8), 1.0, '120V switched receptacle, disposal')
      break
    case 'bathSink':
      add('hot', 'hot', side(-0.33), 1.75)
      add('cold', 'cold', side(0.33), 1.75)
      add('drain', 'drain', 0, 1.4, '1-1/4" trap arm')
      break
    case 'laundrySink':
      add('hot', 'hot', side(-0.4), 2.0)
      add('cold', 'cold', side(0.4), 2.0)
      add('drain', 'drain', 0, 1.5, '1-1/2" trap arm')
      break
    case 'toilet':
      add('cold', 'cold', -0.5, 0.6, '3/8" cold water supply with stop')
      add('drain', 'floorDrain', 0, 0, '3" closet flange at 12" rough-in', 1)
      if (p.bidetSeat === true) add('bidet', 'power', w / 2 + 0.6, 1.0, '120V GFCI receptacle for a bidet seat')
      break
    case 'wallHungToilet': {
      // WALL-HUNG TOILET (2026-10-21): it works through its carrier, never the floor — the
      // supply feeds the concealed TANK (its angle stop is inside, reached through the
      // flush-plate opening), the plate opens the tank's valve, the tank empties down its flush
      // pipe into the bowl's upper spigot, and the bowl's horizontal outlet runs into the
      // carrier's bend, which drops into the floor in the stud bay. No closet flange.
      const r = wallHungRoughIn(wallHungRimIn({ ...t, ...p }))
      add('cold', 'cold', WALL_HUNG.supplyX / 12, r.supplyZ / 12,
        '1/2" cold water to the in-wall tank, top inlet (stop inside the tank, serviced through the flush-plate opening)')
      add('drain', 'drain', 0, r.outletZ / 12,
        `3" (DN 90) waste: the bowl's horizontal outlet into the carrier bend, dropping into the floor ${fmtIn(-r.wasteY)} behind the finished wall (a 2x6 plumbing wall). No closet flange`)
      if (p.bidetSeat === true) add('bidet', 'power', w / 2 + 0.6, r.rim / 12, '120V GFCI receptacle for a bidet seat')
      break
    }
    case 'tubFreestanding': {
      // FREESTANDING TUB (2026-10-06): the drain through the floor at the drain end; the
      // supplies up through the floor at the floor-mounted filler (or, with no filler, to
      // the wall behind for a wall-mounted one)
      const d = num(t.depthFt, 2.67)
      add('drain', 'floorDrain', -w / 2 + 0.75, 0, '1-1/2" waste and overflow, through the floor', d / 2)
      if (t.tubFiller !== false) {
        const fx = -w / 2 - 0.55
        out.push({ key: 'hot', kind: 'hot', x: fx, y: d / 2 - 0.12, z: 0, label: 'Hot water', spec: 'Hot supply up through the floor to the floor-mounted filler (rough-in kit)', floor: true })
        out.push({ key: 'cold', kind: 'cold', x: fx, y: d / 2 + 0.12, z: 0, label: 'Cold water', spec: 'Cold supply up through the floor to the floor-mounted filler (rough-in kit)', floor: true })
      } else {
        add('hot', 'hot', side(-0.4), 2.2, 'Hot supply to a wall-mounted tub filler')
        add('cold', 'cold', side(0.4), 2.2, 'Cold supply to a wall-mounted tub filler')
      }
      break
    }
    case 'tub':
      add('drain', 'floorDrain', -w / 2 + 0.6, 0, 'Tub drain / overflow', num(t.depthFt, 2.5) / 2)
      break
    case 'shower':
      add('drain', 'floorDrain', 0, 0, 'Shower drain', num(t.depthFt, 3) / 2)
      break
    case 'showerValve': {
      const zc = mount ?? 4
      add('hot', 'hot', -0.3, zc)
      add('cold', 'cold', 0.3, zc)
      break
    }
    case 'washer':
      add('hot', 'hot', side(-0.45), 3.5, 'Recessed washer box: hot')
      add('cold', 'cold', side(-0.15), 3.5, 'Recessed washer box: cold')
      add('drain', 'drain', side(0.2), 3.5, 'Recessed washer box: 2" standpipe')
      add('power', 'powerDedicated', side(0.6), 3.5, '120V / 20A dedicated receptacle')
      break
    case 'dryer':
      if (fuel === 'gas') {
        add('gas', 'gas', side(-0.5), 0.5, '1/2" gas stub with shut-off')
        add('power', 'powerDedicated', side(0.5), 3.5, '120V / 20A dedicated receptacle')
      } else add('power', 'power240', side(0.5), 3.5, '240V / 30A dryer receptacle')
      add('vent', 'vent', 0, 0.6, 'Recessed dryer vent box, 4" duct to the exterior')
      break
    case 'washerDryerCombo':
      add('hot', 'hot', side(-0.45), 3.5, 'Recessed washer box: hot')
      add('cold', 'cold', side(-0.15), 3.5, 'Recessed washer box: cold')
      add('drain', 'drain', side(0.2), 3.5, 'Recessed washer box: 2" standpipe')
      if (fuel === 'gas') {
        add('gas', 'gas', side(-0.5), 0.5, '1/2" gas stub with shut-off')
        add('power', 'powerDedicated', side(0.6), 3.5)
      } else add('power', 'power240', side(0.6), 5.5, '240V / 30A receptacle')
      add('vent', 'vent', 0, 0.6, 'Dryer vent, 4" duct to the exterior')
      break
    case 'waterHeater': case 'tanklessWaterHeater': {
      const top = kind === 'waterHeater' ? h : (mount ?? 5) + h / 2
      add('cold', 'cold', side(-0.3), top + 0.5, 'Cold water in')
      add('hot', 'hot', side(0.3), top + 0.5, 'Hot water out')
      if (fuel === 'gas') {
        add('gas', 'gas', side(-0.6), kind === 'waterHeater' ? 1.0 : top - h, '1/2" gas with shut-off and drip leg')
        add('flue', 'flue', 0, top + 1)
        if (kind === 'tanklessWaterHeater') add('power', 'power', side(0.6), top - h, '120V receptacle (controls)')
      } else add('power', 'power240', side(0.6), kind === 'waterHeater' ? 4 : top, '240V dedicated circuit')
      break
    }
    case 'furnace':
      if (fuel === 'gas') add('gas', 'gas', side(-0.6), 1.0, '1/2" gas with shut-off and drip leg')
      add('power', fuel === 'gas' ? 'powerDedicated' : 'power240', side(0.6), 3.5, fuel === 'gas' ? '120V dedicated, service switch' : '240V dedicated circuit')
      if (fuel === 'gas') add('flue', 'flue', 0, h + 0.5)
      add('condensate', 'condensate', side(0.3), 0.5)
      break
    case 'boiler': {
      const zc = mount ?? 5
      add('gas', 'gas', side(-0.5), zc - h / 2 - 0.5, '1/2" gas with shut-off')
      add('power', 'powerDedicated', side(0.5), zc - h / 2 - 0.5, '120V dedicated, service switch')
      add('flue', 'flue', 0, zc + h / 2 + 0.5)
      add('condensate', 'condensate', 0, zc - h / 2 - 0.8)
      break
    }
    case 'airHandler':
      add('power', 'power240', side(0.5), 4, '240V dedicated circuit')
      add('condensate', 'condensate', side(-0.3), 0.5)
      break
    case 'miniSplitHead': {
      const zc = mount ?? 7
      add('lineSet', 'lineSet', side(-0.4), zc)
      add('condensate', 'condensate', side(0.4), zc - 0.3)
      break
    }
    case 'condenser': case 'miniSplitOutdoor':
      add('disconnect', 'disconnect', w / 2 + 0.6, 4, '240V fused disconnect within sight')
      add('lineSet', 'lineSet', side(-0.3), 1)
      break
    case 'fireplaceLinear':
      add('gas', 'gas', side(-w / 2 + 0.5), 0.5, '1/2" gas with key valve')
      add('power', 'power', side(w / 2 - 0.5), 0.5, '120V receptacle (blower / remote)')
      // FIREPLACE FLUES: the stub reads the unit's venting (docs/FIREPLACE_FLUES.md)
      if (el.params.ventType === 'bvent') add('vent', 'flue', 0, h + 0.5, 'B-vent flue through the roof')
      else if (el.params.ventType === 'classA') add('vent', 'flue', 0, h + 0.5, 'Class A chimney through the roof')
      else add('vent', 'flue', 0, h + 0.5, 'Direct-vent termination')
      break
    case 'fireplaceWood': case 'woodStove':
      if (el.params.ventType === 'direct') add('flue', 'flue', 0, h + 1, 'Direct-vent termination')
      else if (el.params.ventType === 'bvent') add('flue', 'flue', 0, h + 1, 'B-vent flue through the roof')
      else add('flue', 'flue', 0, h + 1, 'Class A chimney through the roof')
      break
    case 'outdoorGrill': {
      // OUTDOOR GRILLS (2026-10-08): only the gas styles take gas; the wood and charcoal fires
      // (kamado, Argentine, Santa Maria, offset smoker) take nothing, the built-in Argentine a
      // receptacle for its lights
      const style = String(t.grillStyle || 'builtInGas')
      if (style === 'builtInGas' || style === 'hybridCart') {
        add('gas', 'gas', side(-0.6), 0.75, 'Gas stub with shut-off (natural gas or propane)')
        add('power', 'power', side(0.6), 0.75, '120V GFCI receptacle (rotisserie, lights)')
      } else if (style === 'argentine') {
        add('power', 'power', side(0.6), 0.75, '120V GFCI receptacle (grill lights)')
      }
      break
    }
    case 'hoseBib':
      add('water', 'cold', 0, mount ?? 1.5, 'Frost-free hose bib with vacuum breaker')
      break
    case 'poolEquipment':
      add('power', 'power240', side(w / 2 - 0.4), 4, '240V dedicated circuit, pump / heater (size per equipment)')
      add('gas', 'gas', side(0.1), 0.75, 'Gas to the pool heater')
      add('plumbing', 'poolPlumbing', side(-w / 2 + 0.6), 0.5)
      break
    case 'elevator': {
      // RESIDENTIAL ELEVATOR WIRING (2026-10-06): per drive — see ELEVATOR_DRIVES
      const drive = String(p.elevatorDrive || 'hydraulic')
      const stops = elevatorStops(el)
      const base = el.level?.elevationFt ?? 0
      const bottom = stops[0] ?? { id: el.levelId, name: '', elevationFt: base }
      const top = stops[stops.length - 1] ?? bottom
      const d = num(t.depthFt, 5)
      // the controller side, as seen from the landing facing the door — local +x is
      // that viewer's left
      const seek: 'left' | 'right' = p.elevatorControllerSide === 'right' ? 'right' : 'left'
      const at = (stop: ElevatorStop, key: string, k: HookupKind, y: number, z: number, label: string, spec: string, outside: boolean) =>
        out.push({ key, kind: k, x: 0, y, z: stop.elevationFt - base + z, label, spec, levelId: stop.id, seek, outside })
      if (drive === 'hydraulic' || drive === 'machineRoomLess') {
        const ctl = drive === 'hydraulic' ? bottom : top
        const where = drive === 'hydraulic' ? 'in the machine room' : 'at the controller, top landing'
        const amps = drive === 'hydraulic' ? '40A' : '30A'
        at(ctl, 'main', 'disconnect', d * 0.7, 4.5, 'Main disconnect',
          `208/230V single-phase dedicated circuit (typ. ${amps}), lockable fused disconnect ${where}; confirm with the manufacturer`, true)
        at(ctl, 'cabLight', 'disconnect', d * 0.4, 4.5, 'Cab light disconnect',
          `120V / 15A dedicated cab-light circuit with its own disconnect ${where}`, true)
        at(ctl, 'phone', 'data', d * 0.15, 4.5, 'Phone line',
          'Phone line to the controller for the cab emergency phone (ASME A17.1)', true)
        at(bottom, 'pitOutlet', 'powerDedicated', d * 0.35, 1.5, 'Pit receptacle',
          '120V GFCI duplex receptacle in the pit (NEC 620.85)', false)
        at(bottom, 'pitLight', 'switch', d - 0.6, 4, 'Pit light switch',
          'Pit light, switched from the pit access at the bottom landing (NEC 620.24)', false)
      } else if (drive === 'pneumatic') {
        out.push({
          key: 'main', kind: 'disconnect', x: side(0.6), y: 0, z: top.elevationFt - base + 4.5, levelId: top.id,
          label: 'Main disconnect', spec: '208/230V single-phase dedicated circuit, disconnect at the top unit; confirm with the manufacturer',
        })
      } else {
        out.push({
          key: 'main', kind: 'powerDedicated', x: side(0.6), y: 0, z: 1.5, levelId: bottom.id,
          label: 'Dedicated receptacle', spec: '120V / 20A dedicated receptacle the lift plugs into; confirm with the manufacturer',
        })
      }
      // the controls at every landing door into the shaft (a shaftless lift has none)
      if (drive !== 'throughFloor') out.push(...elevatorCallStations(el, list(), stops))
      break
    }
    default:
      break
  }
  const off = new Set(Array.isArray(p.hookupsOff) ? (p.hookupsOff as string[]) : [])
  for (const hk of out) if (off.has(hk.key)) hk.off = true
  return out
}

/** the hookups an object actually shows (the user's OFF ones dropped) */
export function activeHookups(el: HookupElementLike, others: Iterable<HookupElementLike> = []): Hookup[] {
  return objectHookups(el, others).filter((hk) => !hk.off)
}

/** the kinds whose hookups read their neighbours (a dependency for re-derivation) */
// (the elevator: its call stations read the landing doors)
export const NEIGHBOUR_HOOKUP_KINDS: readonly string[] = ['bed', 'nightstand', 'sofa', 'sectional', 'elevator']

// ---------------------------------------------------------------------------
// HOSTED HOOKUPS (2026-10-05, Matt: "I want the associated items to actually attach to
// the building where they should, meaning usually a wall, a floor, or a ceiling, and not
// stay with the family item. I noticed some night stands were off the wall and so their
// hosted power outlets were off the wall, too."). Each active hookup lands ON the
// building: a wall service on the nearest wall FACE behind its equipment (searched
// straight back, up to WALL_REACH_FT, on a wall standing at that height); with no wall
// there (an island range, a floating nightstand) it comes up through the FLOOR under the
// equipment's back; floor services (drains, floor boxes) on the floor; a hood duct or a
// flue on the CEILING above when there is one. Derived on every read — move the
// nightstand away and its outlet stays on the wall; move the wall and the outlet goes
// with it.
// ---------------------------------------------------------------------------

export type HookupHost = 'wall' | 'floor' | 'ceiling'

export interface PlacedHookup extends Hookup {
  host: HookupHost
  /** the wall / floor / ceiling it attaches to (null = the bare level floor) */
  hostId: string | null
  /** world plan point on the host */
  at: Pt2
  /** unit plan direction the glyph faces (off a wall face into the room; the
   *  equipment's own facing on a floor or ceiling) */
  n: Pt2
  /** absolute height (world feet) */
  zAbs: number
}

/** how far behind the equipment a wall may stand and still take its services */
export const WALL_REACH_FT = 3

function wallSpan(w: HookupElementLike): [number, number] {
  const p = w.params
  const z0 = (w.level?.elevationFt ?? 0) + num(p.baseOffsetFt, 0)
  const top = p.topMode === 'level' ? w.paramLevels?.topLevelId : null
  const z1 = top ? top.elevationFt + num(p.topOffsetFt, 0) : z0 + num(p.unconnectedHeightFt, 9)
  return [z0, z1]
}

/** both finish faces of a wall as world segments, each with its outward normal */
function wallFaces(w: HookupElementLike): { a: Pt2; b: Pt2; n: Pt2 }[] {
  const path = w.params.path as Path2D | null
  if (!path?.nodes || path.nodes.length < 2) return []
  const pts = flattenPathPts(path)
  const delta = centerlineOffsetFt(w.params.locationLine as ParamValue, w.params.flipped as ParamValue, w.assembly ?? null)
  const half = num(w.assemblyThicknessFt, 0.5) / 2
  const out: { a: Pt2; b: Pt2; n: Pt2 }[] = []
  for (let i = 0; i + 1 < pts.length; i++) {
    const a = pts[i], b = pts[i + 1]
    const dx = b[0] - a[0], dy = b[1] - a[1], L = Math.hypot(dx, dy)
    if (L < 1e-6) continue
    const nl: Pt2 = [-dy / L, dx / L]
    for (const sgn of [1, -1] as const) {
      const off = delta + sgn * half
      out.push({ a: [a[0] + nl[0] * off, a[1] + nl[1] * off], b: [b[0] + nl[0] * off, b[1] + nl[1] * off], n: [nl[0] * sgn, nl[1] * sgn] })
    }
  }
  return out
}

function inPoly(p: Pt2, r: Pt2[]): boolean {
  let c = false
  for (let i = 0, j = r.length - 1; i < r.length; j = i++) {
    if ((r[i][1] > p[1]) !== (r[j][1] > p[1]) && p[0] < ((r[j][0] - r[i][0]) * (p[1] - r[i][1])) / (r[j][1] - r[i][1]) + r[i][0]) c = !c
  }
  return c
}

/** the slab (floor or ceiling) on this level whose outline holds the point */
function slabAt(list: HookupElementLike[], cat: 'floor' | 'ceiling', levelId: string, p: Pt2): HookupElementLike | null {
  for (const s of list) {
    if (s.category !== cat || s.levelId !== levelId) continue
    const b = s.params.boundary as Path2D | null
    if (!b?.nodes || b.nodes.length < 3) continue
    if (inPoly(p, flattenPathPts({ ...b, closed: true }))) return s
  }
  return null
}

/** RESIDENTIAL ELEVATOR WIRING: a wall standing at height z across the ray from o along
 *  dir — its face looking back at o, or (outside) that same wall's far face, the room
 *  beyond it */
function sideWallHit(
  walls: HookupElementLike[], faces: (w: HookupElementLike) => { a: Pt2; b: Pt2; n: Pt2 }[],
  o: Pt2, dir: Pt2, zAbs: number, outside: boolean,
): { id: string; at: Pt2; n: Pt2 } | null {
  const reach = Math.max(WALL_REACH_FT, 8)
  const cross = (fc: { a: Pt2; b: Pt2 }): number | null => {
    const ex = fc.b[0] - fc.a[0], ey = fc.b[1] - fc.a[1]
    const den = dir[0] * ey - dir[1] * ex
    if (Math.abs(den) < 1e-9) return null
    const t = ((fc.a[0] - o[0]) * ey - (fc.a[1] - o[1]) * ex) / den
    const s = ((fc.a[0] - o[0]) * dir[1] - (fc.a[1] - o[1]) * dir[0]) / den
    return s < -1e-6 || s > 1 + 1e-6 || t < 0 ? null : t
  }
  let best: { t: number; w: HookupElementLike; n: Pt2 } | null = null
  for (const w of walls) {
    const [z0, z1] = wallSpan(w)
    if (zAbs < z0 - 1e-6 || zAbs > z1 + 1e-6) continue
    for (const fc of faces(w)) {
      if (fc.n[0] * dir[0] + fc.n[1] * dir[1] > -0.2) continue
      const t = cross(fc)
      if (t == null || t > reach) continue
      if (!best || t < best.t) best = { t, w, n: fc.n }
    }
  }
  if (!best) return null
  if (!outside) return { id: best.w.id, at: [o[0] + dir[0] * best.t, o[1] + dir[1] * best.t], n: best.n }
  // the same wall's far face, just beyond the near one
  let far: { t: number; n: Pt2 } | null = null
  for (const fc of faces(best.w)) {
    if (fc.n[0] * dir[0] + fc.n[1] * dir[1] < 0.2) continue
    const t = cross(fc)
    if (t == null || t < best.t - 1e-6 || t > best.t + 3) continue
    if (!far || t < far.t) far = { t, n: fc.n }
  }
  return far ? { id: best.w.id, at: [o[0] + dir[0] * far.t, o[1] + dir[1] * far.t], n: far.n } : null
}

/** the object's ACTIVE hookups, each attached to the wall, floor or ceiling it belongs on */
export function placeHookups(el: HookupElementLike, others: Iterable<HookupElementLike> = []): PlacedHookup[] {
  const list = [...others]
  // GARAGE DOOR OPENER: a hosted door has no frame of its own — its rule places every
  // service itself (all `fixed`)
  if (el.category === 'door') {
    const base = el.level?.elevationFt ?? 0
    return activeHookups(el, list).filter((hk) => hk.fixed).map((hk) => ({
      ...hk, host: hk.fixed!.host ?? 'wall', hostId: hk.fixed!.hostId, at: hk.fixed!.at, n: hk.fixed!.n,
      zAbs: hk.fixed!.zAbs ?? base + hk.z,
    }))
  }
  const f = hookupFrame(el)
  if (!f) return []
  const hooks = activeHookups(el, list)
  if (!hooks.length) return []
  const base = el.level?.elevationFt ?? 0
  const walls = list.filter((w) => w.category === 'wall' && w.id !== el.id)
  const facesOf = new Map<string, { a: Pt2; b: Pt2; n: Pt2 }[]>()
  const faces = (w: HookupElementLike) => {
    let fs = facesOf.get(w.id)
    if (!fs) { fs = wallFaces(w); facesOf.set(w.id, fs) }
    return fs
  }
  const back: Pt2 = [-f.v[0], -f.v[1]]
  const out: PlacedHookup[] = []
  const floorAt = (p: Pt2) => {
    const fl = slabAt(list, 'floor', el.levelId, p)
    return { hostId: fl ? fl.id : null, z: fl ? (fl.level?.elevationFt ?? base) + num(fl.params.heightOffsetFt, 0) : base }
  }
  for (const hk of hooks) {
    const zAbs = base + hk.z
    // RESIDENTIAL ELEVATOR WIRING: already placed by its rule (a hall call station)
    if (hk.fixed) {
      out.push({ ...hk, host: hk.fixed.host ?? 'wall', hostId: hk.fixed.hostId, at: hk.fixed.at, n: hk.fixed.n, zAbs: hk.fixed.zAbs ?? zAbs })
      continue
    }
    // ... or looking to the SIDE for its wall (an elevator's controller-side wall)
    if (hk.seek === 'left' || hk.seek === 'right') {
      const dir: Pt2 = hk.seek === 'left' ? f.u : [-f.u[0], -f.u[1]]
      const hit = sideWallHit(walls, faces, hookupWorld(f, 0, hk.y), dir, zAbs, !!hk.outside)
      if (hit) {
        out.push({ ...hk, host: 'wall', hostId: hit.id, at: hit.at, n: hit.n, zAbs })
        continue
      }
    }
    if (hk.kind === 'floorBox' || hk.kind === 'floorDrain' || hk.floor) {
      const at = hookupWorld(f, hk.x, hk.y)
      const fl = floorAt(at)
      out.push({ ...hk, host: 'floor', hostId: fl.hostId, at, n: f.v, zAbs: fl.z })
      continue
    }
    if (hk.kind === 'flue' || (hk.kind === 'vent' && String(el.typeParams.objectKind) === 'hoodInsert')) {
      const at = hookupWorld(f, hk.x, f.d / 2)
      const ce = slabAt(list, 'ceiling', el.levelId, at)
      if (ce) {
        out.push({ ...hk, host: 'ceiling', hostId: ce.id, at, n: f.v, zAbs: (ce.level?.elevationFt ?? base) + num(ce.params.heightOffsetFt, 8) })
        continue
      }
    }
    // straight back from just inside the equipment's back, to the nearest wall face
    const o = hookupWorld(f, hk.x, 0.5)
    let best: { t: number; at: Pt2; n: Pt2; id: string } | null = null
    for (const w of walls) {
      if (w.levelId !== el.levelId) {
        const [z0, z1] = wallSpan(w)
        if (zAbs < z0 - 1e-6 || zAbs > z1 + 1e-6) continue
      } else {
        const [z0, z1] = wallSpan(w)
        if (zAbs > z1 + 1e-6 || zAbs < z0 - 1e-6) continue
      }
      for (const fc of faces(w)) {
        // the face must look toward the equipment
        if (fc.n[0] * back[0] + fc.n[1] * back[1] > -0.2) continue
        const ex = fc.b[0] - fc.a[0], ey = fc.b[1] - fc.a[1]
        const den = back[0] * ey - back[1] * ex
        if (Math.abs(den) < 1e-9) continue
        const t = ((fc.a[0] - o[0]) * ey - (fc.a[1] - o[1]) * ex) / den
        const s = ((fc.a[0] - o[0]) * back[1] - (fc.a[1] - o[1]) * back[0]) / den
        if (s < -1e-6 || s > 1 + 1e-6 || t < 0 || t > WALL_REACH_FT + 0.5) continue
        if (!best || t < best.t) best = { t, at: [o[0] + back[0] * t, o[1] + back[1] * t], n: fc.n, id: w.id }
      }
    }
    if (best) {
      out.push({ ...hk, host: 'wall', hostId: best.id, at: best.at, n: best.n, zAbs })
      continue
    }
    // no wall behind: up through the floor under the equipment's back
    const at = hookupWorld(f, hk.x, 0.25)
    const fl = floorAt(at)
    out.push({ ...hk, host: 'floor', hostId: fl.hostId, at, n: f.v, zAbs: fl.z })
  }
  return out
}
